import { useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged, signInWithPopup, signOut } from "firebase/auth";
import { Link } from "react-router-dom";
import {
  addPayday,
  addBudgetSource,
  addCutoffPayment,
  deleteBudgetSource,
  deleteCutoffPayment,
  ensureRecurringPaymentsForPeriod,
  loadBudgetState,
  removePayday,
  saveBudgetStateSnapshot,
  toggleCutoffPaymentChecked,
  updateBudgetSource,
  updateCutoffPayment,
} from "../services/budgetStore";
import { auth, googleProvider, hasFirebaseConfig } from "../firebaseClient";
import "./Budget.css";

const formatPeso = (value) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const monthFormatter = new Intl.DateTimeFormat("en-PH", {
  month: "long",
  year: "numeric",
});

const shortMonthFormatter = new Intl.DateTimeFormat("en-PH", {
  month: "short",
  year: "numeric",
});

const monthDayFormatter = new Intl.DateTimeFormat("en-PH", {
  month: "short",
  day: "numeric",
});

const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const addMonths = (date, delta) => {
  const next = new Date(date);
  next.setDate(1);
  next.setMonth(next.getMonth() + delta);
  return next;
};

const toMonthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const toDateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const getCutoffDateRange = (monthDate, cutoff) => {
  if (cutoff === "26-10") {
    const start = new Date(
      monthDate.getFullYear(),
      monthDate.getMonth() - 1,
      26,
    );
    const end = new Date(monthDate.getFullYear(), monthDate.getMonth(), 10);
    return { start, end };
  }

  const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 11);
  const end = new Date(monthDate.getFullYear(), monthDate.getMonth(), 25);
  return { start, end };
};

const formatRange = (monthDate, cutoff) => {
  const { start, end } = getCutoffDateRange(monthDate, cutoff);

  return `${start.toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
  })} - ${end.toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
  })}`;
};

const isWithinRange = (date, start, end) => {
  const compareDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
  const compareStart = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  ).getTime();
  const compareEnd = new Date(
    end.getFullYear(),
    end.getMonth(),
    end.getDate(),
  ).getTime();

  return compareDate >= compareStart && compareDate <= compareEnd;
};

const isSameDate = (left, right) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const buildCalendarDays = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDayOfMonth = new Date(year, month, 1);
  const startWeekday = firstDayOfMonth.getDay();
  const gridStart = new Date(year, month, 1 - startWeekday);

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);

    return {
      key: `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`,
      date,
      day: date.getDate(),
      inMonth: date.getMonth() === month,
    };
  });
};

function BudgetPage() {
  const [state, setState] = useState({
    status: "loading",
    budgetsByPeriod: {},
    paymentsByPeriod: {},
    budgetSourcesByPeriod: {},
    paydaysByMonth: {},
    error: "",
    saveMessage: "",
  });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedCutoff, setSelectedCutoff] = useState("26-10");
  const [paymentForm, setPaymentForm] = useState({
    name: "",
    amount: "",
    recurring: false,
  });
  const [sourceForm, setSourceForm] = useState({
    type: "salary",
    name: "",
    amount: "",
  });
  const [editingSourceId, setEditingSourceId] = useState("");
  const [sourceEditForm, setSourceEditForm] = useState({
    type: "salary",
    name: "",
    amount: "",
  });
  const [isSavingSource, setIsSavingSource] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [editForm, setEditForm] = useState({
    name: "",
    amount: "",
    recurring: false,
  });
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [isSavingData, setIsSavingData] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [user, setUser] = useState(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [authFeedback, setAuthFeedback] = useState("");
  const [isPaydayModalOpen, setIsPaydayModalOpen] = useState(false);
  const [paydayModalDate, setPaydayModalDate] = useState(null);
  const [isSavingPayday, setIsSavingPayday] = useState(false);
  const wheelLockRef = useRef(false);
  const calendarRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const userId = user?.uid || null;

  const selectedPeriodKey = `${toMonthKey(selectedMonth)}(${selectedCutoff})`;

  const cutoffPayments = useMemo(
    () => state.paymentsByPeriod[selectedPeriodKey] || [],
    [selectedPeriodKey, state.paymentsByPeriod],
  );
  const cutoffSources = useMemo(
    () => state.budgetSourcesByPeriod[selectedPeriodKey] || [],
    [selectedPeriodKey, state.budgetSourcesByPeriod],
  );

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const payload = await loadBudgetState(userId);

        if (!isMounted) {
          return;
        }

        setState((current) => ({
          ...current,
          status: "ready",
          budgetsByPeriod: payload.budgetsByPeriod || {},
          paymentsByPeriod: payload.paymentsByPeriod || {},
          budgetSourcesByPeriod: payload.budgetSourcesByPeriod || {},
          paydaysByMonth: payload.paydaysByMonth || {},
          error: "",
        }));
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setState((current) => ({
          ...current,
          status: "error",
          error:
            error instanceof Error
              ? error.message
              : "Failed to load your cutoff budget data.",
        }));
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    if (!hasFirebaseConfig || !auth) {
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setAuthFeedback("");
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    setEditingId("");
    setEditForm({ name: "", amount: "", recurring: false });
    setEditingSourceId("");
    setSourceEditForm({ type: "salary", name: "", amount: "" });
  }, [selectedPeriodKey]);

  useEffect(() => {
    let isActive = true;

    const syncRecurring = async () => {
      try {
        const updated = await ensureRecurringPaymentsForPeriod(
          selectedPeriodKey,
          userId,
        );

        if (!isActive) {
          return;
        }

        applyUpdatedState(updated);
      } catch (error) {
        if (!isActive) {
          return;
        }

        setState((current) => ({
          ...current,
          error:
            error instanceof Error
              ? error.message
              : "Failed to sync recurring payments.",
        }));
      }
    };

    syncRecurring();

    return () => {
      isActive = false;
    };
  }, [selectedPeriodKey, userId]);

  const totalCutoffPayments = useMemo(
    () =>
      cutoffPayments.reduce((sum, entry) => sum + Number(entry.amount || 0), 0),
    [cutoffPayments],
  );

  const checkedPaymentsTotal = useMemo(
    () =>
      cutoffPayments
        .filter((entry) => entry.isChecked)
        .reduce((sum, entry) => sum + Number(entry.amount || 0), 0),
    [cutoffPayments],
  );

  const recurringTotal = useMemo(
    () =>
      cutoffPayments
        .filter((entry) => entry.recurring)
        .reduce((sum, entry) => sum + Number(entry.amount || 0), 0),
    [cutoffPayments],
  );

  const totalSourceAmount = useMemo(
    () =>
      cutoffSources.reduce((sum, entry) => sum + Number(entry.amount || 0), 0),
    [cutoffSources],
  );
  const cutoffBudget = totalSourceAmount;
  const oneTimeTotal = totalCutoffPayments - recurringTotal;
  const remainingBudget = cutoffBudget - totalCutoffPayments;
  const sourceBreakdown = useMemo(() => {
    const map = {};

    cutoffSources.forEach((entry) => {
      const key =
        String(entry.type || "other")
          .trim()
          .toLowerCase() || "other";
      map[key] = Number(map[key] || 0) + Number(entry.amount || 0);
    });

    return Object.entries(map)
      .map(([type, total]) => ({ type, total }))
      .sort((left, right) => right.total - left.total);
  }, [cutoffSources]);

  const setSaveMessage = (message) => {
    setState((current) => ({ ...current, saveMessage: message }));

    setTimeout(() => {
      setState((current) => ({ ...current, saveMessage: "" }));
    }, 1800);
  };

  const showToast = (message) => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage("");
    }, 2200);
  };

  const applyUpdatedState = (updated) => {
    setState((current) => ({
      ...current,
      status: "ready",
      budgetsByPeriod: updated.budgetsByPeriod || {},
      paymentsByPeriod: updated.paymentsByPeriod || {},
      budgetSourcesByPeriod: updated.budgetSourcesByPeriod || {},
      paydaysByMonth: updated.paydaysByMonth || {},
      error: "",
    }));
  };

  const handleSaveBudgetAndSource = async (event) => {
    event.preventDefault();

    const sourceName = sourceForm.name.trim();
    const sourceAmount = Number(sourceForm.amount);

    if (!sourceName) {
      setState((current) => ({
        ...current,
        error: "Source name is required.",
      }));
      return;
    }

    if (!Number.isFinite(sourceAmount) || sourceAmount < 0) {
      setState((current) => ({
        ...current,
        error: "Source amount must be 0 or more.",
      }));
      return;
    }

    try {
      setIsSavingSource(true);
      const updated = await addBudgetSource(
        selectedPeriodKey,
        {
          type: sourceForm.type,
          name: sourceName,
          amount: sourceAmount,
        },
        userId,
      );

      applyUpdatedState(updated);
      setSourceForm({ type: "salary", name: "", amount: "" });
      setSaveMessage("Source added. Cutoff budget updated from sources.");
    } catch (error) {
      setState((current) => ({
        ...current,
        error:
          error instanceof Error ? error.message : "Failed to save source.",
      }));
    } finally {
      setIsSavingSource(false);
    }
  };

  const startEditSource = (source) => {
    setEditingSourceId(source.id);
    setSourceEditForm({
      type: source.type || "other",
      name: source.name,
      amount: String(source.amount || ""),
    });
  };

  const cancelEditSource = () => {
    setEditingSourceId("");
    setSourceEditForm({ type: "salary", name: "", amount: "" });
  };

  const handleSaveSourceEdit = async (sourceId) => {
    const name = sourceEditForm.name.trim();
    const amount = Number(sourceEditForm.amount);

    if (!name) {
      setState((current) => ({
        ...current,
        error: "Source name is required.",
      }));
      return;
    }

    if (!Number.isFinite(amount) || amount < 0) {
      setState((current) => ({
        ...current,
        error: "Source amount must be 0 or more.",
      }));
      return;
    }

    try {
      setIsSavingSource(true);
      const updated = await updateBudgetSource(
        selectedPeriodKey,
        sourceId,
        {
          type: sourceEditForm.type,
          name,
          amount,
        },
        userId,
      );

      applyUpdatedState(updated);
      cancelEditSource();
      setSaveMessage("Source updated.");
    } catch (error) {
      setState((current) => ({
        ...current,
        error:
          error instanceof Error ? error.message : "Failed to update source.",
      }));
    } finally {
      setIsSavingSource(false);
    }
  };

  const handleDeleteSource = async (sourceId) => {
    try {
      const updated = await deleteBudgetSource(
        selectedPeriodKey,
        sourceId,
        userId,
      );
      applyUpdatedState(updated);

      if (editingSourceId === sourceId) {
        cancelEditSource();
      }

      setSaveMessage("Source deleted.");
    } catch (error) {
      setState((current) => ({
        ...current,
        error:
          error instanceof Error ? error.message : "Failed to delete source.",
      }));
    }
  };

  const handleAddPayment = async (event) => {
    event.preventDefault();

    const name = paymentForm.name.trim();
    const amount = Number(paymentForm.amount);

    if (!name) {
      setState((current) => ({
        ...current,
        error: "Payment name is required.",
      }));
      return;
    }

    if (!Number.isFinite(amount) || amount < 0) {
      setState((current) => ({
        ...current,
        error: "Payment amount must be 0 or more.",
      }));
      return;
    }

    try {
      setIsAddingPayment(true);
      const updated = await addCutoffPayment(
        selectedPeriodKey,
        {
          name,
          amount,
          recurring: paymentForm.recurring,
        },
        userId,
      );
      applyUpdatedState(updated);
      setPaymentForm({ name: "", amount: "", recurring: false });
      setSaveMessage("Payment added.");
    } catch (error) {
      setState((current) => ({
        ...current,
        error:
          error instanceof Error ? error.message : "Failed to add payment.",
      }));
    } finally {
      setIsAddingPayment(false);
    }
  };

  const handleToggleChecked = async (payment) => {
    const previousPayments = cutoffPayments;

    const optimisticPayments = cutoffPayments.map((entry) =>
      entry.id === payment.id
        ? { ...entry, isChecked: !payment.isChecked }
        : entry,
    );

    setState((current) => ({
      ...current,
      paymentsByPeriod: {
        ...current.paymentsByPeriod,
        [selectedPeriodKey]: optimisticPayments,
      },
      error: "",
    }));

    try {
      const updated = await toggleCutoffPaymentChecked(
        selectedPeriodKey,
        payment.id,
        !payment.isChecked,
        userId,
      );
      applyUpdatedState(updated);
    } catch (error) {
      setState((current) => ({
        ...current,
        paymentsByPeriod: {
          ...current.paymentsByPeriod,
          [selectedPeriodKey]: previousPayments,
        },
        error:
          error instanceof Error
            ? error.message
            : "Failed to update payment status.",
      }));
    }
  };

  const startEdit = (payment) => {
    setEditingId(payment.id);
    setEditForm({
      name: payment.name,
      amount: String(payment.amount),
      recurring: Boolean(payment.recurring),
    });
  };

  const cancelEdit = () => {
    setEditingId("");
    setEditForm({ name: "", amount: "", recurring: false });
  };

  const handleSaveEdit = async (paymentId) => {
    const name = editForm.name.trim();
    const amount = Number(editForm.amount);

    if (!name) {
      setState((current) => ({
        ...current,
        error: "Payment name is required.",
      }));
      return;
    }

    if (!Number.isFinite(amount) || amount < 0) {
      setState((current) => ({
        ...current,
        error: "Payment amount must be 0 or more.",
      }));
      return;
    }

    try {
      const updated = await updateCutoffPayment(
        selectedPeriodKey,
        paymentId,
        {
          name,
          amount,
          recurring: editForm.recurring,
        },
        userId,
      );
      applyUpdatedState(updated);
      cancelEdit();
      setSaveMessage("Payment updated.");
    } catch (error) {
      setState((current) => ({
        ...current,
        error:
          error instanceof Error ? error.message : "Failed to update payment.",
      }));
    }
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      const updated = await deleteCutoffPayment(
        selectedPeriodKey,
        paymentId,
        userId,
      );
      applyUpdatedState(updated);

      if (editingId === paymentId) {
        cancelEdit();
      }

      setSaveMessage("Payment deleted.");
    } catch (error) {
      setState((current) => ({
        ...current,
        error:
          error instanceof Error ? error.message : "Failed to delete payment.",
      }));
    }
  };

  const changeMonth = (delta) => {
    setSelectedMonth((current) => addMonths(current, delta));
  };

  useEffect(() => {
    const node = calendarRef.current;

    if (!node) {
      return undefined;
    }

    const handleWheel = (event) => {
      event.preventDefault();

      if (wheelLockRef.current) {
        return;
      }

      wheelLockRef.current = true;

      if (event.deltaY < 0) {
        changeMonth(-1);
      } else if (event.deltaY > 0) {
        changeMonth(1);
      }

      setTimeout(() => {
        wheelLockRef.current = false;
      }, 130);
    };

    node.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      node.removeEventListener("wheel", handleWheel);
    };
  }, []);

  const handleCalendarDaySelect = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    let nextCutoff = "11-25";
    let anchorYear = year;
    let anchorMonth = month;

    if (day >= 26) {
      nextCutoff = "26-10";
      anchorMonth += 1;

      if (anchorMonth > 11) {
        anchorMonth = 0;
        anchorYear += 1;
      }
    } else if (day <= 10) {
      nextCutoff = "26-10";
    }

    setSelectedDate(new Date(date));
    setSelectedCutoff(nextCutoff);
    setSelectedMonth(new Date(anchorYear, anchorMonth, 1));
    setPaydayModalDate(new Date(date));
    setIsPaydayModalOpen(true);
  };

  const closePaydayModal = () => {
    setIsPaydayModalOpen(false);
    setPaydayModalDate(null);
  };

  const isPaydayDate = (date) => {
    const dateKey = toDateKey(date);
    const monthKey = toMonthKey(date);
    const entries = state.paydaysByMonth?.[monthKey] || [];
    return entries.includes(dateKey);
  };

  const handleAddPayday = async () => {
    if (!paydayModalDate) {
      return;
    }

    try {
      setIsSavingPayday(true);
      const updated = await addPayday(toDateKey(paydayModalDate), userId);
      applyUpdatedState(updated);
      setSaveMessage("Payday saved.");
      showToast("Payday saved to Firestore.");
      closePaydayModal();
    } catch (error) {
      setState((current) => ({
        ...current,
        error:
          error instanceof Error ? error.message : "Failed to save payday.",
      }));
    } finally {
      setIsSavingPayday(false);
    }
  };

  const handleRemovePayday = async () => {
    if (!paydayModalDate) {
      return;
    }

    try {
      setIsSavingPayday(true);
      const updated = await removePayday(toDateKey(paydayModalDate), userId);
      applyUpdatedState(updated);
      setSaveMessage("Payday removed.");
      showToast("Payday removed.");
      closePaydayModal();
    } catch (error) {
      setState((current) => ({
        ...current,
        error:
          error instanceof Error ? error.message : "Failed to remove payday.",
      }));
    } finally {
      setIsSavingPayday(false);
    }
  };

  const handleSaveAllData = async () => {
    try {
      setIsSavingData(true);
      const updated = await saveBudgetStateSnapshot(
        {
          budgetsByPeriod: state.budgetsByPeriod,
          paymentsByPeriod: state.paymentsByPeriod,
          budgetSourcesByPeriod: state.budgetSourcesByPeriod,
          paydaysByMonth: state.paydaysByMonth,
        },
        userId,
      );

      applyUpdatedState(updated);
      setSaveMessage("Budget data saved to Firestore.");
      showToast("Saved to Firestore.");
    } catch (error) {
      setState((current) => ({
        ...current,
        error:
          error instanceof Error
            ? error.message
            : "Failed to save budget data.",
      }));
    } finally {
      setIsSavingData(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (!auth || !googleProvider) {
      setAuthFeedback("Firebase auth is not configured.");
      return;
    }

    setAuthFeedback("");
    try {
      setIsSigningIn(true);
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      const details = error instanceof Error ? ` (${error.message})` : "";
      setAuthFeedback(`Google sign-in failed.${details}`);
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleLogout = async () => {
    if (!auth) {
      return;
    }

    setAuthFeedback("");
    try {
      await signOut(auth);
      setState((current) => ({
        ...current,
        budgetsByPeriod: {},
        paymentsByPeriod: {},
        budgetSourcesByPeriod: {},
        paydaysByMonth: {},
      }));
    } catch (error) {
      const details = error instanceof Error ? ` (${error.message})` : "";
      setAuthFeedback(`Logout failed.${details}`);
    }
  };

  const monthStrip = useMemo(
    () =>
      Array.from({ length: 13 }, (_, index) => {
        const delta = index - 6;
        const month = addMonths(selectedMonth, delta);
        const key = toMonthKey(month);

        return {
          key,
          label: monthFormatter.format(month),
          delta,
        };
      }),
    [selectedMonth],
  );

  const calendarDays = useMemo(
    () => buildCalendarDays(selectedMonth),
    [selectedMonth],
  );
  const selectedCutoffRange = useMemo(
    () => getCutoffDateRange(selectedMonth, selectedCutoff),
    [selectedMonth, selectedCutoff],
  );
  const cutoffOneRangeLabel = useMemo(
    () => formatRange(selectedMonth, "26-10"),
    [selectedMonth],
  );
  const cutoffTwoRangeLabel = useMemo(
    () => formatRange(selectedMonth, "11-25"),
    [selectedMonth],
  );
  const cutoffRangeTitle = `${monthDayFormatter.format(selectedCutoffRange.start)} - ${monthDayFormatter.format(selectedCutoffRange.end)}`;
  const selectedMonthShortLabel = shortMonthFormatter.format(selectedMonth);

  return (
    <main className="budget-page">
      <section className="budget-hero">
        <div className="budget-hero-top">
          <div className="budget-hero-copy">
            <p className="budget-kicker">Money Planner</p>
            <h1>Cutoff Budget Board</h1>
            <p>
              Budgets are tracked per cutoff, not monthly. Pick a month and
              cutoff, then manage recurring and one-time payments for that exact
              period.
            </p>
          </div>

          {hasFirebaseConfig ? (
            <section className="budget-auth-inline" aria-live="polite">
              {user ? (
                <>
                  <p className="budget-auth-email">
                    Signed in as <strong>{user.email}</strong>
                  </p>
                  <p className="budget-auth-ok">
                    Firestore saves are now linked to your account.
                  </p>
                  <button
                    type="button"
                    className="budget-link-subtle"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <p className="budget-auth-prompt">
                    Sign in with Google to save your budget in your account.
                  </p>
                  <button
                    type="button"
                    className="budget-link-primary"
                    onClick={handleGoogleLogin}
                    disabled={isSigningIn}
                  >
                    {isSigningIn ? "Signing in..." : "Sign in with Google"}
                  </button>
                </>
              )}

              {authFeedback ? (
                <p className="budget-auth-feedback">{authFeedback}</p>
              ) : null}
            </section>
          ) : null}
        </div>

        <div className="budget-links">
          <Link className="budget-link-primary" to="/grocery">
            Open Grocery
          </Link>
          <Link className="budget-link-subtle" to="/">
            Back to Home
          </Link>
        </div>
      </section>

      <section className="budget-panel budget-calendar" ref={calendarRef}>
        <div className="budget-calendar-top">
          <h2>Calendar</h2>
          <p>
            Scroll wheel: up = previous month, down = next month. Infinite month
            navigation.
          </p>
        </div>

        <div className="budget-calendar-nav">
          <button type="button" onClick={() => changeMonth(-1)}>
            Previous Month
          </button>
          <strong>{monthFormatter.format(selectedMonth)}</strong>
          <button type="button" onClick={() => changeMonth(1)}>
            Next Month
          </button>
        </div>

        <div className="budget-month-strip" aria-label="Month chooser">
          {monthStrip.map((entry) => (
            <button
              key={entry.key}
              type="button"
              className={`budget-month-chip${entry.delta === 0 ? " is-active" : ""}`}
              onClick={() => changeMonth(entry.delta)}
            >
              {entry.label}
            </button>
          ))}
        </div>

        <div
          className="budget-cutoff-tabs"
          role="tablist"
          aria-label="Cutoff selector"
        >
          <button
            type="button"
            className={selectedCutoff === "26-10" ? "is-active" : ""}
            onClick={() => setSelectedCutoff("26-10")}
          >
            {`Cutoff 1 (26-10) | ${cutoffOneRangeLabel}`}
          </button>
          <button
            type="button"
            className={selectedCutoff === "11-25" ? "is-active" : ""}
            onClick={() => setSelectedCutoff("11-25")}
          >
            {`Cutoff 2 (11-25) | ${cutoffTwoRangeLabel}`}
          </button>
        </div>

        <div className="budget-weekdays" aria-hidden="true">
          {weekdayLabels.map((label) => (
            <span key={label}>{label}</span>
          ))}
        </div>

        <div className="budget-days-grid" aria-label="Month days">
          {calendarDays.map((entry) => {
            const inCutoff = isWithinRange(
              entry.date,
              selectedCutoffRange.start,
              selectedCutoffRange.end,
            );
            const isSelected = isSameDate(entry.date, selectedDate);
            const isPayday = isPaydayDate(entry.date);

            return (
              <button
                type="button"
                key={entry.key}
                className={`budget-day-cell${entry.inMonth ? "" : " is-outside"}${inCutoff ? " is-cutoff" : ""}${isSelected ? " is-selected" : ""}${isPayday ? " is-payday" : ""}`}
                title={entry.date.toLocaleDateString("en-PH")}
                onClick={() => handleCalendarDaySelect(entry.date)}
              >
                {isPayday ? <span className="budget-payday-dot" /> : null}
                {entry.day}
              </button>
            );
          })}
        </div>

        <p className="budget-period-meta">
          Current period: <strong>{selectedPeriodKey}</strong> | Range:{" "}
          {cutoffRangeTitle}
        </p>
      </section>

      <section className="budget-grid">
        <article className="budget-panel">
          <h2>Set Cutoff Budget & Sources</h2>
          <form className="budget-form" onSubmit={handleSaveBudgetAndSource}>
            <p className="budget-inline-section-label">Cutoff budget</p>
            <p className="budget-status-note">
              Auto-calculated from total sources for this cutoff.
            </p>
            <p className="budget-budget-preview">{formatPeso(cutoffBudget)}</p>

            <p className="budget-inline-section-label">Budget sources</p>
            <label htmlFor="source-type">Source type</label>
            <select
              id="source-type"
              value={sourceForm.type}
              onChange={(event) =>
                setSourceForm((current) => ({
                  ...current,
                  type: event.target.value,
                }))
              }
            >
              <option value="salary">Salary</option>
              <option value="debt">Debt</option>
              <option value="allowance">Allowance</option>
              <option value="business">Business</option>
              <option value="other">Other</option>
            </select>

            <label htmlFor="source-name">Source name</label>
            <input
              id="source-name"
              type="text"
              value={sourceForm.name}
              onChange={(event) =>
                setSourceForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              placeholder="e.g. Primary salary"
            />

            <label htmlFor="source-amount">Amount (PHP)</label>
            <input
              id="source-amount"
              type="number"
              min="0"
              step="0.01"
              value={sourceForm.amount}
              onChange={(event) =>
                setSourceForm((current) => ({
                  ...current,
                  amount: event.target.value,
                }))
              }
              placeholder="e.g. 12000"
            />

            <button type="submit" disabled={isSavingSource}>
              {isSavingSource ? "Saving..." : "Add Source"}
            </button>
          </form>

          {cutoffSources.length > 0 ? (
            <ul className="budget-source-list">
              {cutoffSources.map((source) => {
                const isEditingSource = editingSourceId === source.id;

                if (isEditingSource) {
                  return (
                    <li key={source.id} className="budget-source-row">
                      <div className="budget-edit-wrap">
                        <select
                          value={sourceEditForm.type}
                          onChange={(event) =>
                            setSourceEditForm((current) => ({
                              ...current,
                              type: event.target.value,
                            }))
                          }
                        >
                          <option value="salary">Salary</option>
                          <option value="debt">Debt</option>
                          <option value="allowance">Allowance</option>
                          <option value="business">Business</option>
                          <option value="other">Other</option>
                        </select>
                        <input
                          type="text"
                          value={sourceEditForm.name}
                          onChange={(event) =>
                            setSourceEditForm((current) => ({
                              ...current,
                              name: event.target.value,
                            }))
                          }
                        />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={sourceEditForm.amount}
                          onChange={(event) =>
                            setSourceEditForm((current) => ({
                              ...current,
                              amount: event.target.value,
                            }))
                          }
                        />
                        <div className="budget-item-actions">
                          <button
                            type="button"
                            className="budget-action-save"
                            onClick={() => handleSaveSourceEdit(source.id)}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="budget-action-cancel"
                            onClick={cancelEditSource}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                }

                return (
                  <li key={source.id} className="budget-source-row">
                    <div>
                      <p className="budget-source-name">{source.name}</p>
                      <small className="budget-source-type">
                        {source.type}
                      </small>
                    </div>
                    <strong>{formatPeso(source.amount)}</strong>
                    <div className="budget-item-actions">
                      <button
                        type="button"
                        className="budget-action-edit"
                        onClick={() => startEditSource(source)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="budget-action-delete"
                        onClick={() => handleDeleteSource(source.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="budget-empty">No source yet for this cutoff.</p>
          )}
        </article>

        <article className="budget-panel">
          <h2>Add Payment</h2>
          <form className="budget-form" onSubmit={handleAddPayment}>
            <label htmlFor="payment-name">Payment name</label>
            <input
              id="payment-name"
              type="text"
              value={paymentForm.name}
              onChange={(event) =>
                setPaymentForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              placeholder="e.g. Electric bill / groceries"
              required
            />

            <label htmlFor="payment-amount">Amount (PHP)</label>
            <input
              id="payment-amount"
              type="number"
              min="0"
              step="0.01"
              value={paymentForm.amount}
              onChange={(event) =>
                setPaymentForm((current) => ({
                  ...current,
                  amount: event.target.value,
                }))
              }
              placeholder="e.g. 1500"
              required
            />

            <label
              className="budget-checkbox-inline"
              htmlFor="payment-recurring"
            >
              <input
                id="payment-recurring"
                type="checkbox"
                checked={paymentForm.recurring}
                onChange={(event) =>
                  setPaymentForm((current) => ({
                    ...current,
                    recurring: event.target.checked,
                  }))
                }
              />
              <span>Recurring payment</span>
            </label>

            <button type="submit" disabled={isAddingPayment}>
              {isAddingPayment ? "Adding..." : "Add Payment"}
            </button>
          </form>
        </article>
      </section>

      {state.error ? (
        <p className="budget-status budget-status-error">{state.error}</p>
      ) : null}
      {state.saveMessage ? (
        <p className="budget-status budget-status-ok">{state.saveMessage}</p>
      ) : null}
      {state.status === "loading" ? (
        <p className="budget-status">Loading budget data...</p>
      ) : null}

      <section className="budget-grid budget-grid-lower">
        <article className="budget-panel budget-metrics">
          <h2>{`Overview (${selectedMonthShortLabel} | ${selectedCutoff})`}</h2>
          <div className="budget-metric-cards">
            <div className="budget-metric-card">
              <p>Cutoff budget</p>
              <strong>{formatPeso(cutoffBudget)}</strong>
            </div>
            <div className="budget-metric-card">
              <p>Total sources</p>
              <strong>{formatPeso(totalSourceAmount)}</strong>
            </div>
            <div className="budget-metric-card">
              <p>Total payments</p>
              <strong>{formatPeso(totalCutoffPayments)}</strong>
            </div>
            <div className="budget-metric-card">
              <p>Recurring total</p>
              <strong>{formatPeso(recurringTotal)}</strong>
            </div>
            <div className="budget-metric-card">
              <p>One-time total</p>
              <strong>{formatPeso(oneTimeTotal)}</strong>
            </div>
            <div className="budget-metric-card">
              <p>Checked off</p>
              <strong>{formatPeso(checkedPaymentsTotal)}</strong>
            </div>
            <div className="budget-metric-card">
              <p>Remaining</p>
              <strong className={remainingBudget < 0 ? "budget-negative" : ""}>
                {formatPeso(remainingBudget)}
              </strong>
            </div>
          </div>

          {sourceBreakdown.length > 0 ? (
            <div className="budget-source-breakdown">
              <h3>Source Breakdown</h3>
              <ul>
                {sourceBreakdown.map((entry) => (
                  <li key={entry.type}>
                    <span>{entry.type}</span>
                    <strong>{formatPeso(entry.total)}</strong>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </article>

        <article className="budget-panel budget-list-panel">
          <h2>{`Payments (${selectedMonthShortLabel} | ${selectedCutoff})`}</h2>
          {cutoffPayments.length === 0 ? (
            <p className="budget-empty">No payments in this cutoff yet.</p>
          ) : (
            <ul className="budget-payment-list">
              {cutoffPayments.map((payment) => {
                const isEditing = editingId === payment.id;

                return (
                  <li key={payment.id} className="budget-payment-row">
                    {isEditing ? (
                      <div className="budget-edit-wrap">
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(event) =>
                            setEditForm((current) => ({
                              ...current,
                              name: event.target.value,
                            }))
                          }
                          placeholder="Payment name"
                        />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={editForm.amount}
                          onChange={(event) =>
                            setEditForm((current) => ({
                              ...current,
                              amount: event.target.value,
                            }))
                          }
                          placeholder="Amount"
                        />
                        <label className="budget-checkbox-inline">
                          <input
                            type="checkbox"
                            checked={editForm.recurring}
                            onChange={(event) =>
                              setEditForm((current) => ({
                                ...current,
                                recurring: event.target.checked,
                              }))
                            }
                          />
                          <span>Recurring</span>
                        </label>
                        <div className="budget-item-actions">
                          <button
                            type="button"
                            className="budget-action-save"
                            onClick={() => handleSaveEdit(payment.id)}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="budget-action-cancel"
                            onClick={cancelEdit}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="budget-payment-main">
                          <label className="budget-payment-checkline">
                            <input
                              type="checkbox"
                              checked={payment.isChecked}
                              onChange={() => handleToggleChecked(payment)}
                            />
                            <span
                              className={`budget-payment-name${payment.isChecked ? " budget-payment-name-done" : ""}`}
                            >
                              {payment.name}
                            </span>
                          </label>
                          <small className="budget-payment-tag">
                            {payment.recurring ? "Recurring" : "One-time"}
                          </small>
                        </div>

                        <strong
                          className={`budget-payment-amount${payment.isChecked ? " budget-payment-amount-done" : ""}`}
                        >
                          {formatPeso(payment.amount)}
                        </strong>

                        <div className="budget-item-actions">
                          <button
                            type="button"
                            className="budget-action-edit"
                            onClick={() => startEdit(payment)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="budget-action-delete"
                            onClick={() => handleDeletePayment(payment.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </article>
      </section>

      <section className="budget-bottom-actions">
        <button
          type="button"
          className="budget-link-primary"
          onClick={handleSaveAllData}
          disabled={isSavingData}
        >
          {isSavingData ? "Saving data..." : "Save Data"}
        </button>
      </section>

      {toastMessage ? <div className="budget-toast">{toastMessage}</div> : null}

      {isPaydayModalOpen && paydayModalDate ? (
        <div className="budget-payday-modal-overlay">
          <dialog className="budget-payday-modal" open>
            <h3>{`Payday for ${paydayModalDate.toLocaleDateString("en-PH", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}`}</h3>
            <p>Save this date as payday?</p>
            <div className="budget-payday-modal-actions">
              {isPaydayDate(paydayModalDate) ? (
                <button
                  type="button"
                  className="budget-link-subtle"
                  onClick={handleRemovePayday}
                  disabled={isSavingPayday}
                >
                  {isSavingPayday ? "Removing..." : "Remove Payday"}
                </button>
              ) : (
                <button
                  type="button"
                  className="budget-link-primary"
                  onClick={handleAddPayday}
                  disabled={isSavingPayday}
                >
                  {isSavingPayday ? "Saving..." : "Add Payday"}
                </button>
              )}
              <button
                type="button"
                className="budget-link-subtle"
                onClick={closePaydayModal}
                disabled={isSavingPayday}
              >
                Close
              </button>
            </div>
          </dialog>
        </div>
      ) : null}
    </main>
  );
}

export default BudgetPage;
